/**
 * Annual **series** layers — the catalogue half of S3's time slider.
 *
 * §D5 says a raster row carries `epoch` (the year it *is*) or `epochs` (the years a series
 * row can step to, each naming the layer id that draws it). Those two fields are the whole
 * convention: nothing here invents a year, a tile URL or a product. The slider offers the
 * years the harvester actually published, which for the land-cover stand-in is 2017–2024 —
 * eight, not the nine the client's "2017–2025" asks for, because 2024 is where the bucket
 * ends (docs/NOTES.md).
 *
 * ## Why the epoch rows are hidden from the datasets list
 *
 * `lc_annual` and its eight `lcaYYYY` rows are nine catalogue rows for one dataset. Listing
 * all nine under Land Use would make the reader pick a year from a dropdown *and* from a
 * slider, with nothing saying which won. So the pane lists the **series** row and the
 * slider addresses the years — `epochMemberIds` is what the pane filters on, and
 * `seriesFor` is what keeps the pane's own select reading "Land cover — annual series"
 * while the map is drawing 2019.
 *
 * The epoch rows are not hidden from anything else: §H5's change tool still addresses them
 * by id, and they are still in `/layers` for every picker that wants a single year.
 */
import type { Layer, LayerEpoch } from '../../lib/api';

/** Every layer that carries a published series (`config.epochs`), catalogue order. */
export function seriesLayers(layers: readonly Layer[] | undefined): Layer[] {
  return (layers ?? []).filter((l) => (l.epochs?.length ?? 0) > 0);
}

/**
 * The ids that are a *year of* some series — the rows the datasets list leaves out.
 *
 * Built from the series rows rather than from `epoch != null`, deliberately: a class
 * raster can declare an epoch without belonging to a series (the two WorldCover COGs do),
 * and hiding those would take two real datasets off the list to tidy up nine.
 */
export function epochMemberIds(layers: readonly Layer[] | undefined): Set<string> {
  const ids = new Set<string>();
  for (const series of seriesLayers(layers)) {
    for (const epoch of series.epochs ?? []) ids.add(epoch.layer);
  }
  return ids;
}

/**
 * The series row this layer id belongs to — itself if it is one, else the series that
 * names it as a year, else `null`.
 *
 * This is what makes the slider survive its own use: picking 2019 puts `lca2019` on the
 * map, and the next render has to find its way back to `lc_annual` to know which years are
 * still on offer.
 */
export function seriesFor(
  layers: readonly Layer[] | undefined,
  id: string | null,
): Layer | null {
  if (!id) return null;
  const rows = seriesLayers(layers);
  return (
    rows.find((l) => l.id === id) ??
    rows.find((l) => (l.epochs ?? []).some((e) => e.layer === id)) ??
    null
  );
}

/**
 * Which year of `series` is drawn right now.
 *
 * The series row itself is bound to the **newest** published year (`apps.datasets.seed
 * .lc_annual`: it borrows that year's dataset, so `config.cog` resolves to that year's
 * COG), so a map showing `lc_annual` is showing the last epoch and the slider says so.
 * An id that is neither the series nor one of its years is `-1`, which every caller reads
 * as "the slider is not about this layer".
 */
export function epochIndexOf(series: Layer, drawn: string | null): number {
  const epochs = series.epochs ?? [];
  if (!epochs.length) return -1;
  if (drawn === series.id) return epochs.length - 1;
  return epochs.findIndex((e) => e.layer === drawn);
}

/** The years a series offers, oldest first — the slider's ticks. */
export function epochsOf(series: Layer | null): LayerEpoch[] {
  return series?.epochs ?? [];
}

/**
 * The layer id to **ask about**, for a raster that is drawn.
 *
 * A series row and its newest epoch are the same pixels — `lc_annual` borrows that year's
 * dataset, so `config.cog` resolves to the same COG — but they are not the same *row*, and
 * one difference matters: the series carries no `config.epoch`, which is the field
 * `apps.analysis.rasters.epochs_for` keys on. Asked about the series, `/analysis/insights`
 * therefore has no epoch family to walk and answers with four blocks and no temporal trend
 * — for the one dataset in the catalogue that most obviously has one.
 *
 * So the panel asks about the **year on the slider**: the drawn id where that is already an
 * epoch, and the newest published epoch where the series row itself is drawn. The numbers
 * are identical either way; what changes is that the trend block exists.
 */
export function analysisLayerFor(
  layers: readonly Layer[] | undefined,
  drawn: string | null,
): string | null {
  if (!drawn) return null;
  const series = seriesFor(layers, drawn);
  if (!series || series.id !== drawn) return drawn;
  const epochs = series.epochs ?? [];
  return epochs[epochs.length - 1]?.layer ?? drawn;
}
